import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Plus,
  Bot,
  Cloud,
  Server,
  Zap,
  Brain,
  MonitorSmartphone,
  Eye,
  EyeOff,
  Loader2,
  AlertTriangle,
  Trash2,
  Search,
  Power,
  Ban,
  Pencil,
  Activity,
  Send,
} from "lucide-react";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { RBACAction } from "../components/RBACButton";
import { Tip } from "../components/ui/tooltip";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import { showToast } from "../components/ui/custom-toast";
import { getAPI, postAPI, putAPI, deleteAPI } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import {
  validateMaxLength,
  validateURL,
  validateJSON,
  validateNumberRange,
} from "../../utils/validators";

// ─── Types (mirror backend) ──────────────────────────────────────────────────

type ProviderType =
  | "ANTHROPIC"
  | "OPENAI"
  | "AZURE_OPENAI"
  | "BEDROCK"
  | "GEMINI"
  | "LOCAL_LLM"
  | "GROQ"
  | "NVIDIA_NIM";

interface OrgUnit {
  id: string;
  name: string;
  shortName?: string;
  orgType?: string;
  isActive?: boolean;
}
type FieldType = "PASSWORD" | "STRING" | "URL" | "NUMBER" | "JSON";

interface ProviderField {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  examples?: string[];
}

interface ProviderMeta {
  provider: ProviderType;
  displayName: string;
  fields: ProviderField[];
}

interface LlmConfigSummary {
  id: string;
  name: string;
  provider: ProviderType;
  modelName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  timeoutSeconds?: number;
  maxRetries?: number;
}

interface LlmConfig extends LlmConfigSummary {
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  timeoutSeconds?: number;
  maxRetries?: number;
  updatedAt?: string;
}

interface ApiErrorEntry {
  code: string;
  title?: string;
  detail: string;
  pointer?: string;
  details?: { providerMessage?: string };
}

interface ParsedApiError {
  banner: string;
  details?: string;
  fieldErrors: Record<string, string>;
}

// ─── Provider icons ───────────────────────────────────────────────────────────

const PROVIDER_COLORS: Record<ProviderType, string> = {
  ANTHROPIC: "text-yellow-600",
  OPENAI: "text-emerald-500",
  AZURE_OPENAI: "text-blue-500",
  BEDROCK: "text-orange-500",
  GEMINI: "text-purple-500",
  LOCAL_LLM: "text-cyan-500",
  GROQ: "text-pink-500",
  NVIDIA_NIM: "text-green-500",
};

function ProviderIcon({
  provider,
  size = 18,
}: {
  provider: ProviderType;
  size?: number;
}) {
  const cls = `flex-shrink-0 ${PROVIDER_COLORS[provider] ?? "text-[#64748b]"}`;
  const props = { size, className: cls };
  switch (provider) {
    case "ANTHROPIC":
      return <Brain {...props} />;
    case "OPENAI":
      return <Brain {...props} />;
    case "AZURE_OPENAI":
      return <Cloud {...props} />;
    case "BEDROCK":
      return <Server {...props} />;
    case "GEMINI":
      return <Zap {...props} />;
    case "LOCAL_LLM":
      return <MonitorSmartphone {...props} />;
    case "GROQ":
      return <Zap {...props} />;
    case "NVIDIA_NIM":
      return <Zap {...props} />;
    default:
      return <Bot {...props} />;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// Parse pointer like "/credentials/apiKey" → "apiKey", or "/modelName" → "modelName"
function pointerToFieldName(pointer?: string): string | undefined {
  if (!pointer) return undefined;
  const parts = pointer.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : undefined;
}

// BE marks identifier-style credentials (access keys, client IDs) as PASSWORD type
// for safety, but those aren't truly secret — masking them just makes them harder
// to verify on paste. Only mask fields whose name signals a real secret.
function isTrueSecretField(name: string): boolean {
  return /(secret|password|token|private|apikey)/i.test(name);
}

// Friendlier wording per the spec's error-code → user-message table.
// Falls back to whatever the BE put in `detail` if we don't have a custom mapping.
const ERROR_CODE_MESSAGES: Record<string, string> = {
  "LLM-0001-E": "Invalid credentials. Check your keys/tokens and try again.",
  "LLM-0002-E": "Credentials are valid but missing required permissions.",
  "LLM-0003-E": "Model not found or not available in this region/server.",
  "LLM-0004-E": "Cannot reach the LLM endpoint. Check the URL and network.",
  "LLM-0005-E": "No active LLM is configured.",
  "LLM-0006-E": "Invalid configuration — please check the highlighted fields.",
  "LLM-0010-E": "Configuration not found.",
  "LLM-0011-E": "Provider cannot be changed after creation.",
  "LLM-0012-E": "Cannot delete the active LLM. Activate another first.",
  "LLM-0099-E": "Unexpected error contacting the provider.",
};

// Map an axios error to banner / field errors / expandable details per the spec.
function parseApiError(err: any, provider?: ProviderMeta): ParsedApiError {
  const fieldErrors: Record<string, string> = {};
  const errors: ApiErrorEntry[] = err?.response?.data?.errors ?? [];

  if (errors.length === 0) {
    return {
      banner:
        err?.message === "OFFLINE"
          ? "You appear to be offline. Check your connection and try again."
          : (err?.message ?? "Something went wrong. Please try again."),
      fieldErrors,
    };
  }

  let banner = "";
  let details: string | undefined;

  for (const e of errors) {
    if (e.details?.providerMessage)
      details = details ?? e.details.providerMessage;

    // LLM-0005-E carries an org-specific message from the backend — surface it verbatim.
    if (e.code === "LLM-0005-E") {
      banner = banner || e.detail || ERROR_CODE_MESSAGES["LLM-0005-E"];
      continue;
    }

    if (e.code === "LLM-0006-E" || e.code === "SYS-0002-E") {
      const f = pointerToFieldName(e.pointer);
      if (f) {
        fieldErrors[f] = e.detail;
        continue;
      }
    }

    if (e.code === "LLM-0003-E") {
      fieldErrors.modelName = fieldErrors.modelName ?? e.detail;
    }

    if (e.code === "LLM-0001-E" && provider) {
      // Highlight all credential (non-modelName) fields.
      for (const f of provider.fields) {
        if (f.name !== "modelName") {
          fieldErrors[f.name] = fieldErrors[f.name] ?? "Check this credential.";
        }
      }
    }

    if (e.code === "LLM-0004-E" && provider) {
      const urlField = provider.fields.find(
        (f) => f.name === "baseUrl" || f.name === "endpoint",
      );
      if (urlField) {
        fieldErrors[urlField.name] = fieldErrors[urlField.name] ?? e.detail;
      }
    }

    if (!banner) {
      banner = ERROR_CODE_MESSAGES[e.code] ?? e.detail;
    }
  }

  if (!banner && Object.keys(fieldErrors).length > 0) {
    banner = "Please fix the highlighted fields and try again.";
  }

  return { banner, details, fieldErrors };
}

// ─── ProviderFieldInput ───────────────────────────────────────────────────────

interface ProviderFieldInputProps {
  field: ProviderField;
  value: string;
  onChange: (v: string) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  error?: string;
  isEdit: boolean;
}

function ProviderFieldInput({
  field,
  value,
  onChange,
  showPassword,
  onTogglePassword,
  error,
  isEdit,
}: ProviderFieldInputProps) {
  // Only mask fields that are *actually* secret. AWS Access Key / Client ID etc
  // come back as PASSWORD from the BE but they're identifiers, not secrets.
  const isMasked = field.type === "PASSWORD" && isTrueSecretField(field.name);
  const editPlaceholder =
    isEdit && !value ? "•••••••• (unchanged)" : field.placeholder;
  const fieldName = `llm-cred-${field.name}`;

  const [jsonBlurError, setJsonBlurError] = useState<string | undefined>();
  const handleJsonBlur = () => {
    if (field.type === "JSON" && value.trim()) {
      try {
        JSON.parse(value);
        setJsonBlurError(undefined);
      } catch {
        setJsonBlurError("Must be valid JSON.");
      }
    } else {
      setJsonBlurError(undefined);
    }
  };
  const displayError = error ?? jsonBlurError;

  return (
    <FormField
      label={field.label + (!field.required ? "" : "")}
      required={field.required}
    >
      <div className="space-y-1.5">
        {field.type === "JSON" ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={handleJsonBlur}
            placeholder={field.placeholder ?? '{ "X-Header": "value" }'}
            rows={4}
            spellCheck={false}
            name={fieldName}
            autoComplete="off"
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white font-mono"
          />
        ) : isMasked ? (
          <div className="relative">
            <FormInput
              type={showPassword ? "text" : "password"}
              value={value}
              onChange={onChange}
              placeholder={editPlaceholder}
              name={fieldName}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#475569] dark:hover:text-white/60 transition-colors"
            >
              {showPassword ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
        ) : (
          <FormInput
            type={field.type === "NUMBER" ? "number" : "text"}
            value={value}
            onChange={onChange}
            placeholder={isEdit && !value ? "(unchanged)" : field.placeholder}
            name={fieldName}
            autoComplete="off"
          />
        )}

        {!field.required && (
          <p className="text-xs italic text-[#94a3b8] dark:text-white/30">
            (optional)
          </p>
        )}

        {field.helpText && <p className="text-xs d">{field.helpText}</p>}

        {field.examples && field.examples.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {field.examples.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => onChange(ex)}
                className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors font-mono"
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {displayError && <p className="text-xs text-red-500">{displayError}</p>}
      </div>
    </FormField>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const LS_ORG_KEY = "llm-configurations:org";

export function LlmConfigurations() {
  // ── Org picker state ──────────────────────────────────────────────────────
  const [orgs, setOrgs] = useState<OrgUnit[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    () => localStorage.getItem(LS_ORG_KEY) ?? "",
  );

  // ── Data state ────────────────────────────────────────────────────────────
  const [configs, setConfigs] = useState<LlmConfig[]>([]);
  const [providers, setProviders] = useState<ProviderMeta[]>([]);
  const [activeConfig, setActiveConfig] = useState<
    LlmConfig | null | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Card search ───────────────────────────────────────────────────────────
  const [llmSearch, setLlmSearch] = useState("");

  // ── Form modal ────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editConfig, setEditConfig] = useState<LlmConfig | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderMeta | null>(
    null,
  );
  const [formOrgId, setFormOrgId] = useState<string>("");
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [showPw, setShowPw] = useState<Record<string, boolean>>({});

  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorDetails, setFormErrorDetails] = useState<string | null>(null);
  const [formFieldErrors, setFormFieldErrors] = useState<
    Record<string, string>
  >({});

  // ── Activate modal ────────────────────────────────────────────────────────
  const [showActivate, setShowActivate] = useState(false);
  const [activateTarget, setActivateTarget] = useState<LlmConfigSummary | null>(
    null,
  );
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activateDetails, setActivateDetails] = useState<string | null>(null);

  // ── Deactivate modal ──────────────────────────────────────────────────────
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [deactivateTarget, setDeactivateTarget] =
    useState<LlmConfigSummary | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  // ── Delete modal ──────────────────────────────────────────────────────────
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LlmConfigSummary | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // ── Test connection ───────────────────────────────────────────────────────
  const [testingId, setTestingId] = useState<string | null>(null);

  // ── Chat tester ───────────────────────────────────────────────────────────
  interface ChatResponse {
    content: string;
    modelUsed: string;
    inputTokens: number;
    outputTokens: number;
    latencyMs: number;
  }
  const CHAT_PROMPT_MAX = 5000;
  const [showChat, setShowChat] = useState(false);
  const [chatPrompt, setChatPrompt] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatResponse, setChatResponse] = useState<ChatResponse | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatErrorDetails, setChatErrorDetails] = useState<string | null>(null);

  // ── Fetch accessible orgs once ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgsLoading(true);
      setOrgsError(null);
      try {
        const res = await getAPI(API_URL.ORG_UNITS + "?flat=true");
        if (cancelled) return;
        const raw: OrgUnit[] = res?.data ?? [];
        const list = raw.filter((o) => o.orgType === "ORGANIZATION");
        setOrgs(list);
        const stored = localStorage.getItem(LS_ORG_KEY);
        const initial =
          stored && list.some((o) => o.id === stored)
            ? stored
            : (list[0]?.id ?? "");
        setSelectedOrgId(initial);
      } catch (e: any) {
        if (cancelled) return;
        setOrgsError(
          e?.message === "OFFLINE"
            ? "You appear to be offline."
            : "Failed to load organizations.",
        );
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist org selection so it survives navigation.
  useEffect(() => {
    if (selectedOrgId) localStorage.setItem(LS_ORG_KEY, selectedOrgId);
  }, [selectedOrgId]);

  const selectedOrg = useMemo(
    () => orgs.find((o) => o.id === selectedOrgId),
    [orgs, selectedOrgId],
  );

  // ── Fetch providers once (provider catalog is org-independent) ────────────
  useEffect(() => {
    (async () => {
      try {
        const provRes = await getAPI(`${API_URL.LLM_CONFIGS}/providers`);
        const allowedProviders = new Set(["ANTHROPIC", "OPENAI", "AZURE_OPENAI", "BEDROCK", "GEMINI", "LOCAL_LLM", "GROQ", "NVIDIA_NIM"]);
        setProviders(
          (provRes?.data ?? []).filter((p: ProviderMeta) =>
            allowedProviders.has(p.provider),
          ),
        );
      } catch {
        // Surfaced via banner in the form when providers list is empty.
      }
    })();
  }, []);

  // ── Fetch configs + active for the selected org ───────────────────────────
  const fetchData = useCallback(async () => {
    if (!selectedOrgId) {
      setConfigs([]);
      setActiveConfig(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const q = `?orgId=${encodeURIComponent(selectedOrgId)}`;
      const [listRes, activeRes] = await Promise.all([
        getAPI(`${API_URL.LLM_CONFIGS}${q}`),
        getAPI(`${API_URL.LLM_CONFIGS}/active${q}`),
      ]);
      const summaries: LlmConfigSummary[] = listRes?.data ?? [];
      // Fetch full details for each config in parallel to get maxTokens, temperature, etc.
      const fullConfigs = await Promise.all(
        summaries.map((s) =>
          getAPI(`${API_URL.LLM_CONFIGS}/${s.id}`)
            .then((r) => (r?.data ?? s) as LlmConfig)
            .catch(() => s as LlmConfig),
        ),
      );
      setConfigs(fullConfigs);
      setActiveConfig(activeRes?.data ?? null);
    } catch (e: any) {
      setError(
        e?.message === "OFFLINE"
          ? "You appear to be offline."
          : "Failed to load LLM configurations.",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshList = useCallback(async () => {
    if (!selectedOrgId) return;
    try {
      const q = `?orgId=${encodeURIComponent(selectedOrgId)}`;
      const [listRes, activeRes] = await Promise.all([
        getAPI(`${API_URL.LLM_CONFIGS}${q}`),
        getAPI(`${API_URL.LLM_CONFIGS}/active${q}`),
      ]);
      const summaries: LlmConfigSummary[] = listRes?.data ?? [];
      const fullConfigs = await Promise.all(
        summaries.map((s) =>
          getAPI(`${API_URL.LLM_CONFIGS}/${s.id}`)
            .then((r) => (r?.data ?? s) as LlmConfig)
            .catch(() => s as LlmConfig),
        ),
      );
      setConfigs(fullConfigs);
      setActiveConfig(activeRes?.data ?? null);
    } catch {
      // surfaced by the next page fetch / toast on action
    }
  }, [selectedOrgId]);

  // ── Open Add ──────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditConfig(null);
    setSelectedProvider(null);
    setFormOrgId(selectedOrgId || orgs[0]?.id || "");
    setFormValues({
      maxTokens: "4096",
      temperature: "0.7",
      topP: "1.0",
      timeoutSeconds: "60",
      maxRetries: "3",
    });
    setShowPw({});
    setFormError(null);
    setFormErrorDetails(null);
    setFormFieldErrors({});
    setShowForm(true);
  };

  // ── Open Edit (pre-fetch full config) ─────────────────────────────────────
  const openEdit = async (row: LlmConfigSummary) => {
    setEditLoadingId(row.id);
    try {
      const res = await getAPI(`${API_URL.LLM_CONFIGS}/${row.id}`);
      const cfg: LlmConfig = res?.data;
      const provMeta =
        providers.find((p) => p.provider === cfg.provider) ?? null;
      setEditConfig(cfg);
      setSelectedProvider(provMeta);
      setFormValues({
        name: cfg.name,
        modelName: cfg.modelName,
        maxTokens: String(cfg.maxTokens ?? 4096),
        temperature: String(cfg.temperature ?? 0.7),
        topP: String(cfg.topP ?? 1.0),
        timeoutSeconds: String(cfg.timeoutSeconds ?? 60),
        maxRetries: String(cfg.maxRetries ?? 3),
      });
      setShowPw({});
      setFormError(null);
      setFormErrorDetails(null);
      setFormFieldErrors({});
      setShowForm(true);
    } catch (e: any) {
      const parsed = parseApiError(e);
      showToast("error", parsed.banner);
    } finally {
      setEditLoadingId(null);
    }
  };

  const setField = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (formFieldErrors[name]) {
      setFormFieldErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  const handleProviderChange = (providerValue: string) => {
    const meta = providers.find((p) => p.provider === providerValue) ?? null;
    setSelectedProvider(meta);
    setFormValues((prev) => ({
      name: prev.name ?? "",
      modelName: "",
      maxTokens: prev.maxTokens ?? "4096",
      temperature: prev.temperature ?? "0.7",
      topP: prev.topP ?? "1.0",
      timeoutSeconds: prev.timeoutSeconds ?? "60",
      maxRetries: prev.maxRetries ?? "3",
    }));
    setFormFieldErrors({});
  };

  // ── Save (create / update) ────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedProvider) return;

    setFormError(null);
    setFormErrorDetails(null);

    // Credential fields are everything except modelName (which is a common field).
    const credFields = selectedProvider.fields.filter(
      (f) => f.name !== "modelName",
    );
    const userFilledAnyCred = credFields.some(
      (f) => !!formValues[f.name]?.trim(),
    );
    const validateCreds = !editConfig || userFilledAnyCred;

    const fieldErrors: Record<string, string> = {};

    // Validate name
    const nameValidation = validateMaxLength(
      formValues.name?.trim() ?? "",
      100,
      "Name",
    );
    if (!nameValidation.valid) {
      fieldErrors.name =
        nameValidation.error ||
        "Name is required and must not exceed 100 characters.";
    } else if (!formValues.name?.trim()) {
      fieldErrors.name = "Name is required.";
    }

    // Validate model name
    const modelNameValidation = validateMaxLength(
      formValues.modelName?.trim() ?? "",
      255,
      "Model name",
    );
    if (!modelNameValidation.valid) {
      fieldErrors.modelName =
        modelNameValidation.error ||
        "Model name is required and must not exceed 255 characters.";
    } else if (!formValues.modelName?.trim()) {
      fieldErrors.modelName = "Model name is required.";
    }

    // Validate credentials
    if (validateCreds) {
      for (const f of credFields) {
        const val = formValues[f.name]?.trim() ?? "";
        if (f.required && !val) {
          fieldErrors[f.name] = `${f.label} is required.`;
        }
        if (f.type === "URL" && val) {
          const urlVal = validateURL(val);
          if (!urlVal.valid) {
            fieldErrors[f.name] =
              "Must be a valid URL (e.g. https://example.com/).";
          }
        }
        if (f.type === "JSON" && val) {
          const jsonVal = validateJSON(val);
          if (!jsonVal.valid) {
            fieldErrors[f.name] = jsonVal.error || "Must be valid JSON.";
          }
        }
      }
    }

    // Validate advanced numeric ranges
    const num = (v: string | undefined) => (v?.trim() ? Number(v) : NaN);
    const maxTokens = num(formValues.maxTokens);
    const temperature = num(formValues.temperature);
    const topP = num(formValues.topP);
    const timeoutSeconds = num(formValues.timeoutSeconds);
    const maxRetries = num(formValues.maxRetries);

    if (Number.isFinite(maxTokens)) {
      const tokensVal = validateNumberRange(
        maxTokens,
        1,
        1_000_000,
        "Max tokens",
      );
      if (!tokensVal.valid) {
        fieldErrors.maxTokens =
          tokensVal.error || "Must be between 1 and 1,000,000.";
      }
    }
    if (Number.isFinite(temperature)) {
      const tempVal = validateNumberRange(temperature, 0, 2, "Temperature");
      if (!tempVal.valid) {
        fieldErrors.temperature = tempVal.error || "Must be between 0 and 2.";
      }
    }
    if (Number.isFinite(topP)) {
      const topPVal = validateNumberRange(topP, 0, 1, "Top P");
      if (!topPVal.valid) {
        fieldErrors.topP = topPVal.error || "Must be between 0 and 1.";
      }
    }
    if (Number.isFinite(timeoutSeconds)) {
      const timeoutVal = validateNumberRange(timeoutSeconds, 1, 600, "Timeout");
      if (!timeoutVal.valid) {
        fieldErrors.timeoutSeconds =
          timeoutVal.error || "Must be between 1 and 600.";
      }
    }
    if (Number.isFinite(maxRetries)) {
      const retriesVal = validateNumberRange(maxRetries, 0, 10, "Max retries");
      if (!retriesVal.valid) {
        fieldErrors.maxRetries =
          retriesVal.error || "Must be between 0 and 10.";
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      setFormFieldErrors(fieldErrors);
      return;
    }

    // Build the `credentials` object with JSON fields parsed to objects (not strings).
    // The `type` field is the discriminator the BE uses to validate the shape; it
    // must match the top-level `provider` (BE 400s with LLM-0006-E otherwise).
    const buildCredentials = (type: ProviderType): Record<string, any> => {
      const out: Record<string, any> = { type };
      for (const f of credFields) {
        const v = formValues[f.name]?.trim();
        if (!v) continue;
        if (f.type === "JSON") {
          try {
            out[f.name] = JSON.parse(v);
          } catch {
            /* validated above; ignore here */
          }
        } else if (f.type === "NUMBER") {
          const n = Number(v);
          if (Number.isFinite(n)) out[f.name] = n;
        } else {
          out[f.name] = v;
        }
      }
      return out;
    };

    setFormSaving(true);
    try {
      if (editConfig) {
        // PUT — only changed fields.
        const body: Record<string, any> = {};

        if (formValues.name !== editConfig.name)
          body.name = formValues.name.trim();
        if (formValues.modelName !== editConfig.modelName) {
          body.modelName = formValues.modelName.trim();
        }
        if (Number.isFinite(maxTokens) && maxTokens !== editConfig.maxTokens) {
          body.maxTokens = maxTokens;
        }
        if (
          Number.isFinite(temperature) &&
          temperature !== editConfig.temperature
        ) {
          body.temperature = temperature;
        }
        if (Number.isFinite(topP) && topP !== editConfig.topP) {
          body.topP = topP;
        }
        if (
          Number.isFinite(timeoutSeconds) &&
          timeoutSeconds !== editConfig.timeoutSeconds
        ) {
          body.timeoutSeconds = timeoutSeconds;
        }
        if (
          Number.isFinite(maxRetries) &&
          maxRetries !== editConfig.maxRetries
        ) {
          body.maxRetries = maxRetries;
        }

        if (userFilledAnyCred) {
          // Only send credentials when ALL required cred fields are present.
          const allRequiredFilled = credFields
            .filter((f) => f.required)
            .every((f) => !!formValues[f.name]?.trim());
          if (allRequiredFilled) {
            body.credentials = buildCredentials(editConfig.provider);
          }
        }

        if (Object.keys(body).length === 0) {
          showToast("warning", "No changes to save.");
          setFormSaving(false);
          setShowForm(false);
          return;
        }

        await putAPI(`${API_URL.LLM_CONFIGS}/${editConfig.id}`, body);
        showToast("success", `"${formValues.name.trim()}" updated.`);
      } else {
        if (!formOrgId) {
          setFormError("Pick an organization first.");
          setFormSaving(false);
          return;
        }
        // POST — full body with nested credentials.
        const body: Record<string, any> = {
          orgId: formOrgId,
          name: formValues.name.trim(),
          provider: selectedProvider.provider,
          modelName: formValues.modelName.trim(),
          credentials: buildCredentials(selectedProvider.provider),
        };
        if (Number.isFinite(maxTokens)) body.maxTokens = maxTokens;
        if (Number.isFinite(temperature)) body.temperature = temperature;
        if (Number.isFinite(topP)) body.topP = topP;
        if (Number.isFinite(timeoutSeconds))
          body.timeoutSeconds = timeoutSeconds;
        if (Number.isFinite(maxRetries)) body.maxRetries = maxRetries;

        await postAPI(API_URL.LLM_CONFIGS, body);
        showToast(
          "success",
          `"${body.name}" created. Activate it to start using it.`,
        );
        setSelectedOrgId(formOrgId);
      }

      setShowForm(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(e, selectedProvider);
      setFormError(parsed.banner);
      setFormErrorDetails(parsed.details ?? null);
      if (Object.keys(parsed.fieldErrors).length > 0) {
        setFormFieldErrors(parsed.fieldErrors);
      }
    } finally {
      setFormSaving(false);
    }
  };

  // ── Activate ──────────────────────────────────────────────────────────────
  const openActivate = (cfg: LlmConfigSummary) => {
    setActivateTarget(cfg);
    setActivateError(null);
    setActivateDetails(null);
    setShowActivate(true);
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    setActivating(true);
    setActivateError(null);
    setActivateDetails(null);
    try {
      await postAPI(`${API_URL.LLM_CONFIGS}/${activateTarget.id}/activate`, {});
      showToast("success", `Now using "${activateTarget.name}".`);
      setShowActivate(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(
        e,
        providers.find((p) => p.provider === activateTarget.provider),
      );
      setActivateError(parsed.banner);
      setActivateDetails(parsed.details ?? null);
    } finally {
      setActivating(false);
    }
  };

  // ── Deactivate ────────────────────────────────────────────────────────────
  const openDeactivate = (cfg: LlmConfigSummary) => {
    setDeactivateTarget(cfg);
    setDeactivateError(null);
    setShowDeactivate(true);
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    setDeactivateError(null);
    try {
      await postAPI(
        `${API_URL.LLM_CONFIGS}/${deactivateTarget.id}/deactivate`,
        {},
      );
      showToast("success", `"${deactivateTarget.name}" deactivated.`);
      setShowDeactivate(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(e);
      setDeactivateError(parsed.banner);
    } finally {
      setDeactivating(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const openDelete = (cfg: LlmConfigSummary) => {
    setDeleteTarget(cfg);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAPI(`${API_URL.LLM_CONFIGS}/${deleteTarget.id}`);
      showToast("success", `"${deleteTarget.name}" deleted.`);
      setShowDelete(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(e);
      showToast("error", parsed.banner);
    } finally {
      setDeleting(false);
    }
  };

  // ── Test connection ───────────────────────────────────────────────────────
  const handleTest = async (cfg: LlmConfigSummary) => {
    setTestingId(cfg.id);
    try {
      const res = await postAPI(`${API_URL.LLM_CONFIGS}/${cfg.id}/test`, {});
      const data = res?.data;
      if (data?.ok) {
        showToast("success", `Connection OK (${data.latencyMs ?? "?"} ms).`);
      } else {
        const code = data?.errorCode ? ` [${data.errorCode}]` : "";
        showToast(
          "error",
          (data?.errorMessage ??
            "Connection failed. Check your configuration.") + code,
        );
      }
    } catch (e: any) {
      const parsed = parseApiError(e);
      showToast("error", parsed.banner);
    } finally {
      setTestingId(null);
    }
  };

  // ── Chat tester ───────────────────────────────────────────────────────────
  const openChat = () => {
    setChatPrompt("");
    setChatResponse(null);
    setChatError(null);
    setChatErrorDetails(null);
    setShowChat(true);
  };

  const handleSendChat = async () => {
    const prompt = chatPrompt.trim();
    if (!prompt || chatSending || !selectedOrgId) return;
    setChatSending(true);
    setChatError(null);
    setChatErrorDetails(null);
    setChatResponse(null);
    try {
      const res = await postAPI(API_URL.LLM_CHAT, {
        orgId: selectedOrgId,
        prompt,
      });
      setChatResponse(res?.data ?? null);
    } catch (e: any) {
      const parsed = parseApiError(e);
      setChatError(parsed.banner);
      setChatErrorDetails(parsed.details ?? null);
    } finally {
      setChatSending(false);
    }
  };

  // Filtered configs for card grid
  const filteredConfigs = useMemo(() => {
    if (!llmSearch.trim()) return configs;
    const q = llmSearch.toLowerCase();
    return configs.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.provider.toLowerCase().includes(q) ||
        c.modelName.toLowerCase().includes(q),
    );
  }, [configs, llmSearch]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const credFields =
    selectedProvider?.fields.filter((f) => f.name !== "modelName") ?? [];
  const modelField = selectedProvider?.fields.find(
    (f) => f.name === "modelName",
  );
  const modelExamples = modelField?.examples ?? [];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1>LLM Configurations</h1>
          <p className="text-sm d mt-0.5">
            Configure the LLM provider used for AI features in each
            organization. One active LLM per org.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openChat}
            disabled={loading || !activeConfig || !selectedOrgId}
            title={
              !selectedOrgId
                ? "Pick an organization first"
                : activeConfig
                  ? "Send a test prompt to the active LLM"
                  : "Activate an LLM first"
            }
            className="h-9 flex shrink-0 whitespace-nowrap items-center gap-2 px-4 rounded-xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.03] text-sm font-semibold text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="size-4" />
            Send Test Prompt
          </button>
          <RBACAction module="llm_configuration" action="create">
            <button
              onClick={openAdd}
              disabled={
                loading ||
                providers.length === 0 ||
                orgsLoading ||
                orgs.length === 0
              }
              className="h-9 flex shrink-0 whitespace-nowrap items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              <Plus className="size-4" />
              Add LLM
            </button>
          </RBACAction>
        </div>
      </div>

      {/* Org-fetch error */}
      {orgsError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
          <AlertTriangle className="size-4 flex-shrink-0" />
          <p className="text-sm flex-1">{orgsError}</p>
        </div>
      )}

      {/* No-active banner — only when list has rows but none active */}
      {!loading &&
        !error &&
        selectedOrgId &&
        configs.length > 0 &&
        !activeConfig && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="size-4 flex-shrink-0" />
            <p className="text-sm">
              <span className="font-medium">
                No active LLM for {selectedOrg?.name ?? "this organization"}
              </span>{" "}
              — AI features are disabled. Activate one below.
            </p>
          </div>
        )}

      {/* Pick-an-org prompt — shown before an org is picked */}
      {!orgsLoading && !orgsError && !selectedOrgId ? (
        <div className="w-full rounded-lg border border-border bg-card py-16 flex flex-col items-center gap-4">
          <div className="size-14 rounded-full flex items-center justify-center bg-[#f1f5f9] dark:bg-white/[0.05]">
            <Cloud className="size-6 text-[#64748b] dark:text-white/40" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-[#475569] dark:text-white/60">
              Pick an organization to manage its LLMs
            </p>
            <p className="text-xs text-[#94a3b8] dark:text-white/30">
              LLM configurations are scoped per organization.
            </p>
          </div>
        </div>
      ) : !loading && !error && selectedOrgId && configs.length === 0 ? (
        <div className="w-full rounded-lg border border-border bg-card py-16 flex flex-col items-center gap-4">
          <div className="size-14 rounded-full flex items-center justify-center bg-[#f1f5f9] dark:bg-white/[0.05]">
            <Cloud className="size-6 text-[#64748b] dark:text-white/40" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-[#475569] dark:text-white/60">
              No active LLM configured for{" "}
              {selectedOrg?.name ?? "this organization"}.
            </p>
            <p className="text-xs text-[#94a3b8] dark:text-white/30">
              Connect a provider like Groq, AWS Bedrock or a Local LLM to enable
              AI features.
            </p>
          </div>
          <button
            onClick={openAdd}
            disabled={providers.length === 0}
            className="h-9 flex shrink-0 whitespace-nowrap items-center gap-2 px-4 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            <Plus className="size-4" />
            Add LLM
          </button>
        </div>
      ) : (
        <>
          {/* Search bar */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
            <input
              type="text"
              placeholder="Search configurations"
              value={llmSearch}
              onChange={(e) => setLlmSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:d/50"
            />
          </div>

          {/* Card grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="size-6 animate-spin d/40" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0" />
              <p className="text-sm flex-1">{error}</p>
              <button onClick={fetchData} className="text-sm underline">
                Retry
              </button>
            </div>
          ) : filteredConfigs.length === 0 ? (
            <p className="text-sm d py-10 text-center">
              {llmSearch.trim()
                ? `No configurations match "${llmSearch}"`
                : "No LLMs configured"}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredConfigs.map((cfg) => {
                  const meta = providers.find(
                    (p) => p.provider === cfg.provider,
                  );
                  return (
                    <div
                      key={cfg.id}
                      className={`rounded-xl border bg-card overflow-hidden flex flex-col transition-shadow ${
                        cfg.isActive
                          ? "border-emerald-500/40 shadow-[0_0_0_1px_rgba(16,185,129,0.15),0_4px_24px_rgba(16,185,129,0.12)]"
                          : "border-border shadow-sm"
                      }`}
                    >
                      {/* Card header */}
                      <div className="flex items-center justify-between px-5 pt-5 pb-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="size-10 shrink-0 rounded-xl flex items-center justify-center bg-[#f1f5f9] dark:bg-white/[0.06]">
                            <ProviderIcon provider={cfg.provider} size={22} />
                          </div>
                          <p className="text-sm font-medium text-[#0f172a] dark:text-white truncate">
                            {meta?.displayName ?? cfg.provider}
                          </p>
                        </div>
                        {cfg.isActive ? (
                          <span className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                            <span className="size-1.5 rounded-full bg-emerald-500" />
                            Active
                          </span>
                        ) : (
                          <span className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#f1f5f9] text-[#64748b] dark:bg-white/[0.06] dark:text-white/50">
                            <span className="size-1.5 rounded-full bg-[#94a3b8] dark:bg-white/30" />
                            Inactive
                          </span>
                        )}
                      </div>

                      <div className="h-px bg-border mx-5" />

                      {/* Card body */}
                      <div className="px-5 py-4 flex-1 space-y-3">
                        <div>
                          <p className="font-bold text-[#0f172a] dark:text-white text-[15px] truncate">
                            {cfg.name}
                          </p>
                          <span className="inline-block font-mono text-xs bg-[#f1f5f9] dark:bg-white/[0.06] px-2 py-0.5 rounded text-[#0f172a] dark:text-white max-w-full truncate mt-1.5">
                            {cfg.modelName}
                          </span>
                        </div>

                        {/* Stats list */}
                        <div className="space-y-1.5">
                          {[
                            {
                              label: "Max Tokens",
                              value: cfg.maxTokens ?? "—",
                            },
                            {
                              label: "Temperature",
                              value:
                                cfg.temperature != null
                                  ? cfg.temperature.toFixed(1)
                                  : "—",
                            },
                            {
                              label: "Top P",
                              value:
                                cfg.topP != null ? cfg.topP.toFixed(2) : "—",
                            },
                            {
                              label: "Timeout",
                              value:
                                cfg.timeoutSeconds != null
                                  ? `${cfg.timeoutSeconds}s`
                                  : "—",
                            },
                            {
                              label: "Max Retries",
                              value: cfg.maxRetries ?? "—",
                            },
                          ].map(({ label, value }) => (
                            <div
                              key={label}
                              className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06]"
                            >
                              <span className="text-xs font-medium text-[#0f172a] dark:text-white/85">
                                {label}
                              </span>
                              <span className="text-xs font-semibold text-[#0f172a] dark:text-white/85">
                                {String(value)}
                              </span>
                            </div>
                          ))}
                        </div>

                        <p className="text-xs text-[#0f172a] dark:text-white/85">
                          Created {formatDate(cfg.createdAt)}
                          {cfg.updatedAt && cfg.updatedAt !== cfg.createdAt && (
                            <span className="ml-2 text-[#0f172a] dark:text-white/85">
                              · Updated {formatDate(cfg.updatedAt)}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Card footer — actions */}
                      <div className="border-t border-border px-4 py-2.5 flex flex-wrap items-center gap-2 bg-[#fafafa] dark:bg-white/[0.02]">
                        <Tip label="Test connection">
                          <button
                            onClick={() => handleTest(cfg)}
                            disabled={testingId === cfg.id}
                            className="flex shrink-0 whitespace-nowrap items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-600 dark:border-violet-500/25 dark:bg-violet-500/10 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors disabled:opacity-40"
                          >
                            {testingId === cfg.id ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                              <Activity className="size-3.5" />
                            )}
                            <span className="text-[11px] font-semibold">
                              Test
                            </span>
                          </button>
                        </Tip>

                        <Tip
                          label={cfg.isActive ? "Already active" : "Activate"}
                        >
                          <button
                            onClick={() => openActivate(cfg)}
                            disabled={cfg.isActive}
                            className="flex shrink-0 whitespace-nowrap items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Power className="size-3.5" />
                            <span className="text-[11px] font-semibold">
                              Activate
                            </span>
                          </button>
                        </Tip>

                        <Tip
                          label={
                            cfg.isActive
                              ? "Deactivate"
                              : "Only the active LLM can be deactivated"
                          }
                        >
                          <button
                            onClick={() => openDeactivate(cfg)}
                            disabled={!cfg.isActive}
                            className="flex shrink-0 whitespace-nowrap items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Ban className="size-3.5" />
                            <span className="text-[11px] font-semibold">
                              Deactivate
                            </span>
                          </button>
                        </Tip>

                        <RBACAction module="llm_configuration" action="update">
                          <Tip label="Edit">
                            <button
                              onClick={() => openEdit(cfg)}
                              disabled={editLoadingId === cfg.id}
                              className="flex shrink-0 whitespace-nowrap items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600 dark:border-indigo-500/25 dark:bg-indigo-500/10 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors disabled:opacity-40"
                            >
                              {editLoadingId === cfg.id ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <Pencil className="size-3.5" />
                              )}
                              <span className="text-[11px] font-semibold">
                                Edit
                              </span>
                            </button>
                          </Tip>
                        </RBACAction>

                        <RBACAction module="llm_configuration" action="delete">
                          <Tip
                            label={
                              cfg.isActive
                                ? "Activate another LLM first to delete this one."
                                : "Delete"
                            }
                          >
                            <button
                              onClick={() => openDelete(cfg)}
                              disabled={cfg.isActive}
                              className="flex shrink-0 whitespace-nowrap items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-600 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="size-3.5" />
                              <span className="text-[11px] font-semibold">
                                Delete
                              </span>
                            </button>
                          </Tip>
                        </RBACAction>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs d">
                Showing <strong>1–{filteredConfigs.length}</strong> of{" "}
                <strong>{configs.length}</strong> configurations
              </p>
            </>
          )}
        </>
      )}

      {/* ──────────────────────────────────────────────────────────────────────
          Form Modal  (Add / Edit)
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showForm}
        onClose={() => !formSaving && setShowForm(false)}
        title={
          editConfig ? `Edit — ${editConfig.name}` : "Add LLM Configuration"
        }
        description={
          editConfig
            ? "Update the connection details for this provider."
            : "Configure the connection details for this provider."
        }
        size="lg"
        closable={!formSaving}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowForm(false)}
            onSubmit={handleSave}
            submitLabel={formSaving ? "Saving…" : "Save"}
            submitDisabled={formSaving || !selectedProvider}
          />
        }
      >
        <div className="space-y-4">
          {/* Provider dropdown — only on create. On edit, the read-only badge below shows the provider. */}
          {!editConfig && (
            <FormField
              label="Provider"
              required
              hint="LLM provider for this configuration."
            >
              <FormSelect
                value={selectedProvider?.provider ?? ""}
                onChange={(v) => handleProviderChange(v)}
                options={[
                  { value: "", label: "Select a provider" },
                  ...providers.map((p) => ({
                    value: p.provider,
                    label: p.displayName,
                  })),
                ]}
              />
            </FormField>
          )}

          {/* Fields shown only after a provider is selected */}
          {selectedProvider && (
            <div className="space-y-4">
              {/* Top-level error banner */}
              {formError && (
                <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 overflow-hidden">
                  <div className="flex items-start gap-3 p-3 text-red-700 dark:text-red-400">
                    <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{formError}</p>
                  </div>
                  {formErrorDetails && (
                    <div className="px-3 pb-3 pt-0 space-y-1">
                      <p className="text-[11px] font-semibold tracking-wide text-red-700/70 dark:text-red-400/70">
                        Provider response
                      </p>
                      <pre className="text-xs p-2.5 rounded-md bg-white/60 dark:bg-black/20 whitespace-pre-wrap break-words border border-red-200/60 dark:border-red-500/20 leading-relaxed text-[#475569] dark:text-white/70 font-mono">
                        {formErrorDetails}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Provider badge */}
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06]">
                <ProviderIcon provider={selectedProvider.provider} size={16} />
                <span className="text-sm font-medium text-[#0f172a] dark:text-white">
                  {selectedProvider.displayName}
                </span>
                <span className="ml-auto text-xs text-[#94a3b8] dark:text-white/30">
                  {editConfig ? "Provider (read-only)" : "Selected provider"}
                </span>
              </div>

              {/* Organization — only on create, shown after provider is selected */}
              {!editConfig && (
                <FormField
                  label="Organization"
                  required
                  hint="The organization this LLM will be configured for."
                >
                  <FormSelect
                    value={formOrgId}
                    onChange={(v) => setFormOrgId(v)}
                    options={
                      orgsLoading
                        ? [{ value: "", label: "Loading organizations…" }]
                        : orgs.length === 0
                          ? [{ value: "", label: "No organizations available" }]
                          : orgs.map((o) => ({ value: o.id, label: o.name }))
                    }
                  />
                </FormField>
              )}

              {/* Name */}
              <FormField
                label="Name"
                required
                hint="A friendly display name for this configuration."
              >
                <FormInput
                  value={formValues.name ?? ""}
                  onChange={(v) => setField("name", v)}
                  placeholder="e.g. Production OpenAI"
                  name="llm-config-name"
                  autoComplete="off"
                />
                {formFieldErrors.name && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {formFieldErrors.name}
                  </p>
                )}
              </FormField>

              {/* Model name (extracted from fields[]) */}
              <FormField label={modelField?.label ?? "Model Name"} required>
                <div className="space-y-1.5">
                  <FormInput
                    value={formValues.modelName ?? ""}
                    onChange={(v) => setField("modelName", v)}
                    placeholder={modelField?.placeholder ?? "e.g. gpt-4o"}
                    name="llm-model-id"
                    autoComplete="off"
                  />
                  {modelField?.helpText && (
                    <p className="text-xs d">{modelField.helpText}</p>
                  )}
                  {modelExamples.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {modelExamples.map((ex) => (
                        <button
                          key={ex}
                          type="button"
                          onClick={() => setField("modelName", ex)}
                          className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors font-mono"
                        >
                          {ex}
                        </button>
                      ))}
                    </div>
                  )}
                  {formFieldErrors.modelName && (
                    <p className="text-xs text-red-500">
                      {formFieldErrors.modelName}
                    </p>
                  )}
                </div>
              </FormField>

              {/* Provider-specific credential fields */}
              {credFields.map((f) => (
                <ProviderFieldInput
                  key={f.name}
                  field={f}
                  value={formValues[f.name] ?? ""}
                  onChange={(v) => setField(f.name, v)}
                  showPassword={showPw[f.name] ?? false}
                  onTogglePassword={() =>
                    setShowPw((prev) => ({ ...prev, [f.name]: !prev[f.name] }))
                  }
                  error={formFieldErrors[f.name]}
                  isEdit={!!editConfig}
                />
              ))}

              {/* Parameters section */}
              <div className="relative flex items-center gap-3">
                <div className="h-px flex-1 bg-[#e2e8f0] dark:bg-white/[0.07]" />
                <span className="text-xs font-medium text-[#94a3b8] dark:text-white/30 tracking-wider">Parameters</span>
                <div className="h-px flex-1 bg-[#e2e8f0] dark:bg-white/[0.07]" />
              </div>

              <FormField label="Max Tokens" hint="1 — 1,000,000">
                <FormInput
                  type="number"
                  value={formValues.maxTokens ?? "4096"}
                  onChange={(v) => setField("maxTokens", v)}
                  placeholder="4096"
                  name="llm-max-tokens"
                  autoComplete="off"
                />
                {formFieldErrors.maxTokens && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {formFieldErrors.maxTokens}
                  </p>
                )}
              </FormField>

              <FormField
                label={`Temperature — ${parseFloat(formValues.temperature ?? "0.7").toFixed(1)}`}
                hint="Controls randomness. Lower = more deterministic, higher = more creative."
              >
                <input
                  type="range"
                  min={0}
                  max={2}
                  step={0.1}
                  value={formValues.temperature ?? "0.7"}
                  onChange={(e) =>
                    setField("temperature", e.target.value)
                  }
                  className="w-full h-2 bg-[#e2e8f0] dark:bg-white/[0.07] rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
                <div className="flex justify-between text-xs text-[#94a3b8] dark:text-white/30 mt-1 px-0.5">
                  <span>0</span>
                  <span>1</span>
                  <span>2</span>
                </div>
                {formFieldErrors.temperature && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {formFieldErrors.temperature}
                  </p>
                )}
              </FormField>

              <FormField
                label={`Top P — ${parseFloat(formValues.topP ?? "1.0").toFixed(2)}`}
                hint="Nucleus sampling threshold."
              >
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={formValues.topP ?? "1.0"}
                  onChange={(e) => setField("topP", e.target.value)}
                  className="w-full h-2 bg-[#e2e8f0] dark:bg-white/[0.07] rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
                <div className="flex justify-between text-xs text-[#94a3b8] dark:text-white/30 mt-1 px-0.5">
                  <span>0</span>
                  <span>1</span>
                </div>
                {formFieldErrors.topP && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {formFieldErrors.topP}
                  </p>
                )}
              </FormField>

              <FormField label="Timeout (seconds)" hint="1 — 600">
                <FormInput
                  type="number"
                  value={formValues.timeoutSeconds ?? "60"}
                  onChange={(v) => setField("timeoutSeconds", v)}
                  placeholder="60"
                  name="llm-timeout-seconds"
                  autoComplete="off"
                />
                {formFieldErrors.timeoutSeconds && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {formFieldErrors.timeoutSeconds}
                  </p>
                )}
              </FormField>

              <FormField label="Max Retries" hint="0 — 10">
                <FormInput
                  type="number"
                  value={formValues.maxRetries ?? "3"}
                  onChange={(v) => setField("maxRetries", v)}
                  placeholder="3"
                  name="llm-max-retries"
                  autoComplete="off"
                />
                {formFieldErrors.maxRetries && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {formFieldErrors.maxRetries}
                  </p>
                )}
              </FormField>
            </div>
          )}
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Activate Confirmation Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showActivate}
        onClose={() => !activating && setShowActivate(false)}
        title={`Activate "${activateTarget?.name}"?`}
        size="sm"
        closable={!activating}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowActivate(false)}
            onSubmit={handleActivate}
            submitLabel={activating ? "Activating…" : "Activate"}
            submitDisabled={activating}
          />
        }
      >
        <div className="space-y-3">
          {activeConfig && activeConfig.id !== activateTarget?.id ? (
            <p className="text-sm text-[#475569] dark:text-white/60">
              This will deactivate{" "}
              <span className="font-medium text-[#0f172a] dark:text-white">
                "{activeConfig.name}"
              </span>
              . All AI features will start using{" "}
              <span className="font-medium text-[#0f172a] dark:text-white">
                "{activateTarget?.name}"
              </span>{" "}
              immediately.
            </p>
          ) : (
            <p className="text-sm text-[#475569] dark:text-white/60">
              All AI features will start using{" "}
              <span className="font-medium text-[#0f172a] dark:text-white">
                "{activateTarget?.name}"
              </span>{" "}
              immediately.
            </p>
          )}

          {activating && (
            <div className="flex items-center gap-2 text-sm text-[#64748b] dark:text-white/50">
              <Loader2 className="size-4 animate-spin" />
              Verifying connection with provider…
            </div>
          )}

          {activateError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 overflow-hidden">
              <div className="flex items-start gap-2 p-3 text-red-700 dark:text-red-400 text-sm">
                <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                <p>{activateError}</p>
              </div>
              {activateDetails && (
                <div className="px-3 pb-3 pt-0 space-y-1">
                  <p className="text-[11px] font-semibold tracking-wide text-red-700/70 dark:text-red-400/70">
                    Provider response
                  </p>
                  <pre className="text-xs p-2.5 rounded-md bg-white/60 dark:bg-black/20 whitespace-pre-wrap break-words border border-red-200/60 dark:border-red-500/20 leading-relaxed text-[#475569] dark:text-white/70 font-mono">
                    {activateDetails}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Deactivate Confirmation Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showDeactivate}
        onClose={() => !deactivating && setShowDeactivate(false)}
        title={`Deactivate "${deactivateTarget?.name}"?`}
        size="sm"
        closable={!deactivating}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowDeactivate(false)}
            onSubmit={handleDeactivate}
            submitLabel={deactivating ? "Deactivating…" : "Deactivate"}
            submitDisabled={deactivating}
          />
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-[#475569] dark:text-white/60">
            This will deactivate{" "}
            <span className="font-medium text-[#0f172a] dark:text-white">
              "{deactivateTarget?.name}"
            </span>
            . AI features will be disabled for this organization until another
            LLM is activated.
          </p>

          {deactivating && (
            <div className="flex items-center gap-2 text-sm text-[#64748b] dark:text-white/50">
              <Loader2 className="size-4 animate-spin" />
              Deactivating…
            </div>
          )}

          {deactivateError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm">
              <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
              <p>{deactivateError}</p>
            </div>
          )}
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Delete Confirmation Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showDelete}
        onClose={() => !deleting && setShowDelete(false)}
        title={`Delete "${deleteTarget?.name}"?`}
        size="sm"
        closable={!deleting}
        footer={
          <div className="flex items-center justify-end gap-3 w-full">
            <button
              onClick={() => setShowDelete(false)}
              disabled={deleting}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-40 shadow-md shadow-red-500/20"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-[#475569] dark:text-white/60">
          This action cannot be undone. All configuration data for{" "}
          <span className="font-medium text-[#0f172a] dark:text-white">
            "{deleteTarget?.name}"
          </span>{" "}
          will be permanently removed.
        </p>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Chat Tester Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showChat}
        onClose={() => !chatSending && setShowChat(false)}
        title="Send Test Prompt"
        description={
          activeConfig
            ? `Sends to ${selectedOrg?.name ? `${selectedOrg.name}'s ` : "the "}active LLM — ${activeConfig.name} (${activeConfig.modelName}).`
            : "No active LLM."
        }
        size="lg"
        closable={!chatSending}
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <span className="text-xs text-[#94a3b8] dark:text-white/30">
              {chatPrompt.length}/{CHAT_PROMPT_MAX}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowChat(false)}
                disabled={chatSending}
                className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
              >
                Close
              </button>
              <button
                onClick={handleSendChat}
                disabled={
                  chatSending ||
                  !chatPrompt.trim() ||
                  chatPrompt.length > CHAT_PROMPT_MAX
                }
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 flex items-center gap-2"
              >
                {chatSending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Send className="size-3.5" />
                )}
                {chatSending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <FormField label="Prompt" required hint="Up to 5,000 characters.">
            <textarea
              value={chatPrompt}
              onChange={(e) =>
                setChatPrompt(e.target.value.slice(0, CHAT_PROMPT_MAX))
              }
              placeholder="Ask the model anything to verify it's responding…"
              rows={5}
              spellCheck={false}
              disabled={chatSending}
              className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white disabled:opacity-50"
            />
          </FormField>

          {/* Error */}
          {chatError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 overflow-hidden">
              <div className="flex items-start gap-2 p-3 text-red-700 dark:text-red-400 text-sm">
                <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                <p>{chatError}</p>
              </div>
              {chatErrorDetails && (
                <div className="px-3 pb-3 pt-0 space-y-1">
                  <p className="text-[11px] font-semibold tracking-wide text-red-700/70 dark:text-red-400/70">
                    Provider response
                  </p>
                  <pre className="text-xs p-2.5 rounded-md bg-white/60 dark:bg-black/20 whitespace-pre-wrap break-words border border-red-200/60 dark:border-red-500/20 leading-relaxed text-[#475569] dark:text-white/70 font-mono">
                    {chatErrorDetails}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Loading */}
          {chatSending && !chatResponse && !chatError && (
            <div className="flex items-center gap-2 text-sm text-[#64748b] dark:text-white/50">
              <Loader2 className="size-4 animate-spin" />
              Waiting for response…
            </div>
          )}

          {/* Response */}
          {chatResponse && (
            <div className="space-y-2">
              <p className="text-xs font-semibold tracking-wide text-[#64748b] dark:text-white/40">
                Response
              </p>
              <pre className="text-sm p-3 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] whitespace-pre-wrap break-words border border-[#e2e8f0] dark:border-white/[0.07] leading-relaxed text-[#0f172a] dark:text-white max-h-[40vh] overflow-y-auto">
                {chatResponse.content}
              </pre>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#64748b] dark:text-white/40">
                <span className="font-mono text-[#475569] dark:text-white/60">
                  {chatResponse.modelUsed}
                </span>
                <span>•</span>
                <span>
                  {chatResponse.inputTokens} in / {chatResponse.outputTokens}{" "}
                  out
                </span>
                <span>•</span>
                <span>{chatResponse.latencyMs} ms</span>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
